import { Checkbox, FormControl, FormControlLabel } from '@mui/material';
import { FormikProps } from 'formik';
import React from 'react';
import { v4 } from 'uuid';
import * as Yup from 'yup';

import {
  CONTRACT_CHECKBOX_LABEL_MAX_LENGTH,
  FormBodyAPI,
  SignatureComponentType,
} from 'src/constants';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { BaseTextField } from 'src/legacy/components/TextField';

interface FormValues {
  componentType: SignatureComponentType | null;
  label: string;
  isOptional: boolean;
}

const getSubmitValues = () => Promise.resolve({ key: v4() });

export const useClientInputLabelForm = ({
  componentType,
  isRequired,
}: {
  componentType: SignatureComponentType | null;
  isRequired: boolean;
}) => {
  // A checkbox reads as a control on its own, so its label is optional even
  // though the label modal is otherwise used for required-label fields.
  const isCheckbox = componentType === SignatureComponentType.REQUEST_CHECKBOX;

  // Checkbox labels are short captions; text fields allow far more.
  const labelMaxLength = isCheckbox ? CONTRACT_CHECKBOX_LABEL_MAX_LENGTH : 4000;

  let labelSchema: Yup.StringSchema<string> = Yup.string().trim();
  if (isRequired && !isCheckbox) {
    labelSchema = labelSchema.required('Label is required');
  }
  if (componentType === SignatureComponentType.REQUEST_TEXT || isCheckbox) {
    labelSchema = labelSchema.max(
      labelMaxLength,
      `Maximum ${labelMaxLength} characters`,
    );
  }

  const validationScheme = Yup.object().shape({ label: labelSchema });

  const FormRenderer: React.FC<FormBodyAPI<FormValues>> = (props) => {
    // without typecasting we get an eslint error when trying to set field values
    const { touched, errors, values, setFieldValue, handleChange, handleBlur } =
      props as FormikProps<FormValues>;

    return (
      <div>
        <FormControl fullWidth>
          <BaseTypography fontType="13Medium">Label</BaseTypography>
          <BaseTextField
            name="label"
            onChange={handleChange}
            onBlur={handleBlur}
            helperText={touched && errors ? touched.label && errors.label : ''}
            variant="outlined"
            error={Boolean(touched.label && errors.label)}
            inputProps={{
              'data-testid': 'label-input',
              maxLength: labelMaxLength + 1,
            }}
            autoFocus
          />
        </FormControl>
        {/* Required/optional toggle for text and checkbox client inputs */}
        {(values.componentType === SignatureComponentType.REQUEST_TEXT ||
          values.componentType === SignatureComponentType.REQUEST_CHECKBOX) && (
          <div className="mt-4">
            <FormControlLabel
              control={
                <Checkbox
                  checked={values.isOptional}
                  name="isOptional"
                  onChange={() =>
                    setFieldValue('isOptional', !values.isOptional)
                  }
                  color="primary"
                  data-testid="isOptional-checkbox"
                />
              }
              label="Make field optional"
              id="make-field-optional-checkbox"
            />
          </div>
        )}
      </div>
    );
  };

  return { validationScheme, getSubmitValues, FormRenderer };
};
